import React, { MouseEvent } from "react";

import { Link, makeStyles, Theme } from "@material-ui/core";
import * as Sentry from "@sentry/browser";
import * as Redux from "react-redux";

import { useBranding } from "client/hooks/useBranding";
import { useMerchantModel } from "client/hooks/useMerchantModel";
import { useTranslation } from "client/hooks/useTranslation";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { CountryList, Industry, Product } from "client/lib/apt-merchant";
import { errorActions } from "client/store";
import { Branding } from "client/utils/branding";

function buildParams(params: { [key: string]: string }) {
  let paramString = "";
  Object.keys(params).forEach((key: string) => {
    paramString = `${paramString}&${key}=${encodeURIComponent(params[key])}`;
  });
  return paramString;
}

const buildLink = ({
  country,
  industry,
  product,
}: {
  country?: CountryList;
  industry?: Industry;
  product?: Product;
}): string => {
  /*
    When product === "Cash", we're pulling the Cash amendment agreement
    which is a separate agreement that is signed on the /app/cash form for
    existing merchants (Batch 1) and is not dependent on industry value
    Cash App Pay feature is currently only supported in the US
  */
  if (product == "Cash" && country === "United States") {
    return `/merchant-agreement/fullpage?${buildParams({
      country: country,
      product: product,
    })}`;
  }

  if (
    !country ||
    !industry ||
    (product === "Cash" && country !== "United States")
  ) {
    return "";
  }

  return `/merchant-agreement/fullpage?${buildParams({
    country: country,
    industry: industry,
    product: product ?? "Afterpay",
  })}`;
};

const useStyles = makeStyles((theme: Theme) => ({
  link: {
    color: theme.palette.info.main,
    fontFamily: "Italian Plate No2 Expanded",
  },
}));

export type AgreementLinkOrigin = "Agreement" | "FAQ";

export const MerchantAgreementLink: React.FC<{
  linkOrigin?: AgreementLinkOrigin;
  product: Product;
}> = ({ linkOrigin, product }) => {
  const { merchant } = useMerchantModel();
  const { brandKey } = useBranding();
  const t = useTranslation();
  const classes = useStyles();

  const determineAgreementLinkLabel = (
    product: Product,
    brandTitle: Branding
  ) =>
    product === "Afterpay"
      ? t(`uiMerchantAgreementLink_${brandTitle}`)
      : "Merchant Agreement";

  const DisabledMerchantAgreementLink: React.FC<{
    label: string;
    linkOrigin?: AgreementLinkOrigin;
  }> = ({ label, linkOrigin }) => {
    const classes = useStyles();
    const dispatch = Redux.useDispatch();

    return (
      <Link
        className={classes.link}
        onClick={(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          dispatch(
            errorActions.setError(t("uiMerchantAgreementLink_Bad_Link"))
          );

          Sentry.captureException(
            new Error(
              `Bad Merchant Agreement Link from clicked from origin: ${linkOrigin?.toString()}, merchantRef: ${
                merchant.merchantAccount.mlsProspectRef
              }, merchantId: ${
                merchant.merchantAccount?.merchantId ?? "N/A"
              }, industry: ${merchant.storeDetails.industry.value}, country: ${
                merchant.companyInformation.registeredCountry.value
              }, product: ${product}`
            )
          );
          logAmplitudeEvent(
            "MERCHANT_AGREEMENT",
            `Bad Merchant Agreement Link from clicked from origin: ${linkOrigin?.toString()}`
          );
        }}
      >
        {label}
      </Link>
    );
  };

  const country =
    merchant.companyInformation.registeredCountry.value ??
    merchant.lead.registeredCountry.value;
  const industry =
    merchant.storeDetails.industry.value ?? merchant.lead.industry.value;
  const label = determineAgreementLinkLabel(product, brandKey);

  const link = buildLink({ country, industry, product });

  // When a merchant doesn't have a country or industry, we
  // don't want the link to open a new page.
  // instead, open a toast to tell them to fill out the company information
  // form
  if (link === "") {
    return (
      <DisabledMerchantAgreementLink label={label} linkOrigin={linkOrigin} />
    );
  }

  return (
    <Link
      className={classes.link}
      href={link}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() =>
        product === "Cash"
          ? logAmplitudeEvent("CASH_PAY_FORM", "cash_pay_agreement_opened")
          : logAmplitudeEvent(
              "REDESIGN_ADD_YOUR_STORE",
              "merchant_agreement_opened"
            )
      }
    >
      {label}
    </Link>
  );
};
